import { type ReactNode, useCallback, useMemo } from 'react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Icon, Tag } from '@timeedit/ui-components';
import {
  Dropdown,
  type MenuProps,
  Typography,
} from '@timeedit/ui-components/antd';
import SortableTag, { type SortableTagItem } from './SortableTag';
import './SortableTagList.scss';

export type SortableTagListProps<T = Record<string, unknown>> = {
  items: SortableTagItem<T>[];
  onReorder?: (items: SortableTagItem<T>[]) => void;
  onRemove?: (item: SortableTagItem<T>, index: number) => void;
  addMenuItems?: MenuProps['items'];
  onAddMenuSelect?: (key: string) => void;
  addButtonLabel?: ReactNode;
  showAddButton?: boolean;
  removable?: boolean;
  sortable?: boolean;
  renderTagContent?: (item: SortableTagItem<T>, index: number) => ReactNode;
  className?: string;
  tagClassName?: string;
  tagStyle?: React.CSSProperties;
  emptyPlaceholder?: ReactNode;
  dragHandleAriaLabel?: string;
  removeAriaLabel?: string;
  previewLabel?: ReactNode;
  renderPreview?: (items: SortableTagItem<T>[]) => ReactNode;
};

const SortableTagList = <T = Record<string, unknown>,>({
  items,
  onReorder,
  onRemove,
  addMenuItems,
  onAddMenuSelect,
  addButtonLabel = 'Add',
  showAddButton = true,
  removable = true,
  sortable = true,
  renderTagContent,
  className,
  tagClassName,
  tagStyle,
  emptyPlaceholder,
  dragHandleAriaLabel = 'Drag to reorder',
  removeAriaLabel = 'Remove item',
  previewLabel,
  renderPreview,
}: SortableTagListProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const itemIds = useMemo(() => items.map((item) => item.id), [items]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((t) => t.id === active.id);
        const newIndex = items.findIndex((t) => t.id === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        onReorder?.(reordered);
      }
    },
    [items, onReorder]
  );

  const containerClassName = className
    ? `sortable-tag-list ${className}`
    : 'sortable-tag-list';

  return (
    <>
      <div className={containerClassName}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={itemIds} strategy={rectSortingStrategy}>
            {items.length === 0 && emptyPlaceholder}
            {items.map((item, index) => (
              <SortableTag<T>
                key={item.id}
                item={item}
                index={index}
                onRemove={onRemove}
                removable={removable}
                sortable={sortable}
                renderContent={renderTagContent}
                className={tagClassName}
                style={tagStyle}
                dragHandleAriaLabel={dragHandleAriaLabel}
                removeAriaLabel={removeAriaLabel}
              />
            ))}
          </SortableContext>
        </DndContext>
        {showAddButton && addMenuItems && (
          <Dropdown
            menu={{
              items: addMenuItems,
              onClick: (info) => onAddMenuSelect?.(info.key),
            }}
            trigger={['click']}
          >
            <Tag
              className="sortable-tag-list__add-button"
              icon={<Icon id="PlusOutlined" alt="Plus" />}
            >
              {addButtonLabel}
            </Tag>
          </Dropdown>
        )}
      </div>
      {(previewLabel || renderPreview) && (
        <div className="sortable-tag-list__preview">
          {previewLabel && (
            <Typography.Text strong>{previewLabel} </Typography.Text>
          )}
          {renderPreview?.(items)}
        </div>
      )}
    </>
  );
};

export default SortableTagList;
